// Package imports:
import React, { useContext, useEffect, useMemo, useState } from "react";
// Component imports:
import CompanyTitle from "../Company/CompanyTitle/CompanyTitle";
import { AccessTokenContext } from "../../contexts/AccessTokenContext";
import { displayAddress, getTableDate, insertDash, translateSurveyStatus } from "../../services/utils";
import Link from "../../ui-elements/Link/Link";
import { OlderDataViewModel, RiskScoreDto } from "../../types/DueDiligenceTypes";
import Table from "../../ui-elements/Table/Table";
import Button from "../../ui-elements/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { riskToLabelTypeMap } from "./CompaniesTable";
import Label from "../../ui-elements/Label/Label";
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import { AnswerListOverviewDto } from "../../types/CompanyAnswersTypes";
import GoBackLink from "../../ui-elements/GoBackLink/GoBackLink";
import Bugsnag from "@bugsnag/js";
import Alert from "../../ui-elements/Alert/Alert";
import InfoHeader from "../../ui-elements/InfoHeader/InfoHeader";

const OlderDataPage: React.FC<OlderDataViewModel> = ({ customerId, warningMessage, company, user }) => {
    const accessToken = useContext(AccessTokenContext);
    const [data, setData] = useState<RiskScoreDto[] | null | Error>(null);
    const [answers, setAnswers] = useState<AnswerListOverviewDto | null>(null);
    const [viewRiskscore, setViewRiskscore] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/DueDiligence/GetRiskscores/${customerId}${user ? '?isIndividual=true' : ''}`);
                if (!response.ok) {
                    //check for erro http status
                    if (response.status === 404) {
                        setData([]);
                        setLoading(false);
                        return;
                    } else if (response.status === 403) {
                        setData(new Error("Þú hefur ekki aðgang að þessum gögnum."));
                        setLoading(false);
                        return;
                    }


                    throw new Error(`${await response.text()}`);
                }

                const data: RiskScoreDto[] = await response.json();
                setData(data);
                setLoading(false);
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    setData(e);
                }
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        const fetchAnswers = async () => {
            if (viewRiskscore === null) {
                setAnswers(null);
                return;
            }
            try {
                setLoading(true);
                const response = await fetch(`/DueDiligence/GetKYCAnswers?riskscoreId=${viewRiskscore}`);
                const answers: AnswerListOverviewDto = await response.json();
                setAnswers(answers);
                setLoading(false);
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    // setAnswers(e);
                }
            }
        }
        fetchAnswers();
    }, [viewRiskscore]);

    const infoHeaderData = useMemo(() => {
        if ((user === null && company === null) || data instanceof Error) {
            return [];
        }

        const infoHeaderData = [{
            title: 'Nafn',
            value: user ? user.firstname + ' ' + user.lastname : company ? company.name : ''
        }];

        if (user?.ssn) {
            infoHeaderData.push({
                title: 'Kennitala',
                value: insertDash(user.ssn)
            });
        } else if (company?.ssn) {
            infoHeaderData.push({
                title: 'Kennitala',
                value: insertDash(company.ssn)
            });
        }

        if (user) {
            infoHeaderData.push({
                title: 'Póstfang',
                value: displayAddress(user.address, user.areaCode, user.region, user.country) || ''
            });
            return infoHeaderData;
        } else if (company) {
            infoHeaderData.push({
                title: 'Póstfang',
                value: displayAddress(company.address, company.areaCode, company.region, company.country) || ''
            });
        }

        return infoHeaderData;
    }, [data]);

    return (
        <div className="KCL_DueDiligence-OlderData">
            <div className="KCL_DueDiligence-OlderData__content">
                <div className="shell page_content">
                    {!warningMessage &&
                        <>
                            <div className="section__title">
                                <div className="section__header--left">
                                    { // Can't be ternary because otherwise double clicking occurs
                                        viewRiskscore &&
                                        <Link onClick={() => setViewRiskscore(null)} icon="back">Fara til baka</Link>
                                    }
                                    {!viewRiskscore &&
                                        <GoBackLink fallbackUrl="/Areidanleikakannanir/Yfirlit" />
                                    }
                                </div>
                            </div>
                            {company && company.ssn &&
                                <CompanyTitle id={company.ssn} accessToken={accessToken} />
                            }
                            {company && !company.ssn &&
                                <InfoHeader data={infoHeaderData} />
                            }
                            {user &&
                                <InfoHeader data={infoHeaderData} />
                            }
                            {!viewRiskscore
                                ? (data instanceof Error)
                                    ? <Alert type="error" headText="Villa kom upp" text={data.message} />
                                    : <>
                                        <Table
                                            tableSize="lg"
                                            showEmptyTableText
                                            withPagination={true}
                                            paginationConfig={{ itemsPerPage: 10, dropdownOutside: true }}
                                            aSide={<h6>Allar áreiðanleikakannanir sem hafa verið framkvæmdar á viðskiptavini.</h6>}
                                            showLoading={loading}
                                            data={data}
                                            columns={[{
                                                title: 'Könnun hafin',
                                                renderCell: (test) => {
                                                    if (test.reportTimestamp) {
                                                        const date = new Date(test.reportTimestamp);
                                                        return getTableDate(date, "DD/MM/YYYY", ".");
                                                        // return <Link href={`/DueDiligence/GetKYCReport?customerId=${customerId}&reportId=${keldanReportId}`} target="_blank" rel="noreferrer" icon="pdf">{getTableDate(date, "DD/MM/YYYY", ".")}</Link>;
                                                    } else {
                                                        return '-';
                                                        // return <Link disabled href={`/DueDiligence/GetKYCReport?customerId=${customerId}&reportId=${keldanReportId}`} target="_blank" rel="noreferrer" icon="pdf">-</Link>;
                                                    }
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Könnun send',
                                                renderCell: ({ dateSent }) => {
                                                    if (dateSent) {
                                                        const date = new Date(dateSent);
                                                        return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                                                    }
                                                    return '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Könnun svarað',
                                                renderCell: ({ answeredTimestamp }) => {
                                                    if (answeredTimestamp) {
                                                        const date = new Date(answeredTimestamp);
                                                        return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                                                    }
                                                    return '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Áhættumat uppfært',
                                                renderCell: ({ riskscoreFinishedTimestamp }) => {
                                                    if (riskscoreFinishedTimestamp) {
                                                        const date = new Date(riskscoreFinishedTimestamp);
                                                        return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                                                    }
                                                    return '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Áhættumat',
                                                renderCell: ({ riskscore }) => {
                                                    const displayString = riskscore ? riskscore.split(' ')[0] : null;
                                                    if (displayString === null) return null;
                                                    let labelType = riskToLabelTypeMap[displayString];
                                                    if (labelType === undefined) return null;
                                                    return <div>
                                                        <Label
                                                            labelType={labelType}
                                                            text={displayString}
                                                            size="lg"
                                                        />
                                                    </div>
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Staða',
                                                renderCell: ({ status }) => translateSurveyStatus(status),
                                                textAlign: 'left'
                                            }, {
                                                title: '',
                                                renderCell: ({ id, answered }) => answered ? <Button size="sm" buttonType="secondary" onClick={() => setViewRiskscore(id)}>Skoða</Button> : '',
                                                textAlign: 'right'
                                            }]}
                                        />
                                    </>
                                : null
                            }
                            {viewRiskscore &&
                                <>
                                    <div>
                                        <Table
                                            tableSize="lg"
                                            showEmptyTableText
                                            withPagination="auto"
                                            paginationConfig={{ itemsPerPage: 10, dropdownOutside: true }}
                                            aSide={<h6>Tengiliðir sem hafa svarað áreiðanleikakönnun fyrir hönd viðskiptavinar.</h6>}
                                            showLoading={loading}
                                            data={answers ? answers.answerLists : []}
                                            columns={[{
                                                title: 'Titill',
                                                renderCell: ({ title }) => {
                                                    return title;
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Notandi',
                                                renderCell: ({ userName }) => {
                                                    return userName;
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Kennitala',
                                                renderCell: ({ userSSN }) => {
                                                    return userSSN;
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Dags. send',
                                                renderCell: ({ dateSent }) => {
                                                    if (dateSent) {
                                                        const date = new Date(dateSent);
                                                        return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                                                    }
                                                    return '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Lokið',
                                                renderCell: ({ finished }) => {
                                                    return finished ? <FontAwesomeIcon icon={faCheck} color="#4569EE" /> : '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Dags. lokið',
                                                renderCell: ({ finishedTimestamp }) => {
                                                    if (finishedTimestamp) {
                                                        const date = new Date(finishedTimestamp);
                                                        return isNaN(date.getTime()) ? '' : getTableDate(date, "DD/MM/YYYY", ".");
                                                    }
                                                    return '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: 'Ógilt',
                                                renderCell: ({ voided }) => {
                                                    return voided ? <FontAwesomeIcon icon={faXmark} color="#FF7697" /> : '';
                                                },
                                                textAlign: 'left'
                                            }, {
                                                title: '',
                                                renderCell: ({ answerlistId }) => {
                                                    if (user) {
                                                        return <Button size="sm" buttonType="secondary" anchorProps={{ href: `/Areidanleikakannanir/Skoda-Svor-Einstaklingar?riskscoreId=${viewRiskscore}&customerId=${customerId}&answerListId=${answerlistId}` }}>Skoða</Button>;
                                                    } else if (company) {
                                                        return <Button size="sm" buttonType="secondary" anchorProps={{ href: `/Areidanleikakannanir/Skoda-Svor-Logadilar?riskscoreId=${viewRiskscore}&customerId=${customerId}&answerListId=${answerlistId}` }}>Skoða</Button>;
                                                    }
                                                    return '';
                                                },
                                                textAlign: 'right'
                                            }]}
                                        />
                                    </div>
                                </>
                            }
                        </>
                    }
                    {warningMessage &&
                        <HeadSection
                            title={warningMessage}
                        />
                    }
                </div>
            </div>
        </div>
    );
}

export default OlderDataPage;